import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

// ============ Types ============

interface InventoryItem {
  id: string;
  partNumber: string;
  description: string;
  supplierPartNumber: string | null;
  inventoryGroup: string | null;
  onHand: number;
  minQuantity: number;
  binLocation: string | null;
  imageFilename: string | null;
  displayUnit: string;
  conversionFactor: number;
  updatedAt: string | null;
}

interface InventoryGroup {
  name: string;
  itemCount: number;
}

type AdjustReason = 'Damaged' | 'Used in Production' | 'Found' | 'Count Correction' | 'Other';

const ADJUST_REASONS: AdjustReason[] = ['Damaged', 'Used in Production', 'Found', 'Count Correction', 'Other'];

// ============ Helpers ============

function formatUpdatedAt(dateStr: string | null): { label: string; dateLabel: string; stale: boolean } {
  if (!dateStr) return { label: 'Never updated', dateLabel: '', stale: true };
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return { label: 'Never updated', dateLabel: '', stale: true };
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const stale = diffDays > 90;
  const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  let relative: string;
  if (diffDays === 0) relative = 'today';
  else if (diffDays === 1) relative = 'yesterday';
  else if (diffDays < 7) relative = `${diffDays}d ago`;
  else if (diffDays < 30) relative = `${Math.floor(diffDays / 7)}w ago`;
  else if (diffDays < 365) relative = `${Math.floor(diffDays / 30)}mo ago`;
  else relative = `${Math.floor(diffDays / 365)}y ago`;
  return { label: `${dateLabel} (${relative})`, dateLabel, stale };
}

function formatQty(qty: number, displayUnit: string, conversionFactor: number): string {
  const safeQty = Number(qty) || 0;
  if (displayUnit && displayUnit !== 'each' && conversionFactor > 1) {
    const displayVal = safeQty / conversionFactor;
    return `${displayVal.toFixed(1)} ${displayUnit}`;
  }
  return Number.isInteger(safeQty) ? String(safeQty) : safeQty.toFixed(1);
}

// ============ Toast ============

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`px-4 py-3 rounded-lg shadow-lg text-sm font-medium max-w-xs ${
            t.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
          onClick={() => onDismiss(t.id)}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ============ Adjust Modal ============

function AdjustModal({
  item,
  onClose,
  onSubmit,
}: {
  item: InventoryItem;
  onClose: () => void;
  onSubmit: (adjustmentType: 'add' | 'subtract', quantity: number, reason: string, notes: string) => Promise<void>;
}) {
  const [adjustType, setAdjustType] = useState<'add' | 'subtract'>('subtract');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState<AdjustReason>('Count Correction');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const hasDisplayUnit = item.displayUnit && item.displayUnit !== 'each' && item.conversionFactor > 1;
  const qtyNum = parseFloat(quantity) || 0;
  // Convert entered display units (yards) to storage units (inches) for the calculation
  const storageQty = hasDisplayUnit ? qtyNum * item.conversionFactor : qtyNum;
  const newQty = adjustType === 'add' ? item.onHand + storageQty : item.onHand - storageQty;
  const isValid = qtyNum > 0 && reason && (adjustType === 'add' || newQty >= 0);

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    try {
      // Send storage units (inches) to API, not display units (yards)
      await onSubmit(adjustType, storageQty, reason, notes);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-600" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-bold text-white">Adjust Stock</h3>
          <p className="text-sm text-gray-400 mt-1">{item.partNumber} - {item.description}</p>
          <p className="text-sm text-gray-400">
            Current: <span className="text-white font-medium">{formatQty(item.onHand, item.displayUnit, item.conversionFactor)}</span>
          </p>
        </div>

        <div className="p-4 space-y-4">
          {/* Adjustment Type Toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setAdjustType('subtract')}
              className={`py-3 rounded-lg font-medium text-center ${
                adjustType === 'subtract'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-700 text-gray-300'
              }`}
            >
              - Remove
            </button>
            <button
              onClick={() => setAdjustType('add')}
              className={`py-3 rounded-lg font-medium text-center ${
                adjustType === 'add'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-300'
              }`}
            >
              + Add
            </button>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              {hasDisplayUnit ? `Quantity (${item.displayUnit})` : 'Quantity'}
            </label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="1"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              placeholder={`Enter ${hasDisplayUnit ? item.displayUnit : 'quantity'}`}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-lg focus:border-yellow-400 focus:outline-none"
              autoFocus
            />
          </div>

          {/* Preview */}
          {qtyNum > 0 && (
            <div className={`p-3 rounded-lg text-center text-sm font-medium ${
              newQty < 0 ? 'bg-red-900/50 text-red-300' : 'bg-gray-700 text-gray-200'
            }`}>
              {formatQty(item.onHand, item.displayUnit, item.conversionFactor)}
              {' '}&rarr;{' '}
              {formatQty(newQty, item.displayUnit, item.conversionFactor)}
              {newQty < 0 && ' (cannot go below 0)'}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Reason</label>
            <div className="grid grid-cols-2 gap-2">
              {ADJUST_REASONS.map(r => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${
                    reason === r
                      ? 'bg-yellow-400 text-gray-900'
                      : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Additional notes..."
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-lg bg-gray-700 text-white font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || submitting}
            className="flex-1 py-3 rounded-lg bg-yellow-400 text-gray-900 font-bold disabled:opacity-50"
          >
            {submitting ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Receive Modal ============

function ReceiveModal({
  item,
  onClose,
  onSubmit,
}: {
  item: InventoryItem;
  onClose: () => void;
  onSubmit: (quantity: number, quantityInDisplayUnits: boolean, poNumber: string, notes: string) => Promise<void>;
}) {
  const hasDisplayUnit = item.displayUnit && item.displayUnit !== 'each' && item.conversionFactor > 1;
  const [quantity, setQuantity] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const qtyNum = parseFloat(quantity) || 0;
  const isValid = qtyNum > 0;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    try {
      await onSubmit(qtyNum, !!hasDisplayUnit, poNumber, notes);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-600" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-bold text-white">Receive Stock</h3>
          <p className="text-sm text-gray-400 mt-1">{item.partNumber} - {item.description}</p>
          <p className="text-sm text-gray-400">
            Current: <span className="text-white font-medium">{formatQty(item.onHand, item.displayUnit, item.conversionFactor)}</span>
          </p>
        </div>

        <div className="p-4 space-y-4">
          {/* Quantity */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              {hasDisplayUnit ? `${item.displayUnit.charAt(0).toUpperCase() + item.displayUnit.slice(1)} Received` : 'Quantity Received'}
            </label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="1"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              placeholder={`Enter ${hasDisplayUnit ? item.displayUnit : 'quantity'}`}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-lg focus:border-yellow-400 focus:outline-none"
              autoFocus
            />
          </div>

          {/* Preview */}
          {qtyNum > 0 && (
            <div className="p-3 rounded-lg text-center text-sm font-medium bg-green-900/50 text-green-300">
              {formatQty(item.onHand, item.displayUnit, item.conversionFactor)}
              {' '}&rarr;{' '}
              {hasDisplayUnit
                ? formatQty(item.onHand + qtyNum * item.conversionFactor, item.displayUnit, item.conversionFactor)
                : formatQty(item.onHand + qtyNum, item.displayUnit, item.conversionFactor)
              }
            </div>
          )}

          {/* PO Number */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">PO Number (optional)</label>
            <input
              type="text"
              value={poNumber}
              onChange={e => setPoNumber(e.target.value)}
              placeholder="e.g. PO-12345"
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Additional notes..."
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-lg bg-gray-700 text-white font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || submitting}
            className="flex-1 py-3 rounded-lg bg-green-600 text-white font-bold disabled:opacity-50"
          >
            {submitting ? 'Receiving...' : 'Receive'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Barcode Scanner ============

function BarcodeScanner({ onScan, onClose }: { onScan: (code: string) => void; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [manualInput, setManualInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setScanning(true);
        }

        // Try native BarcodeDetector
        if ('BarcodeDetector' in window) {
          const detector = new (window as any).BarcodeDetector({
            formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'qr_code']
          });
          const scanLoop = async () => {
            if (cancelled || !videoRef.current) return;
            try {
              const barcodes = await detector.detect(videoRef.current);
              if (barcodes.length > 0) {
                const code = barcodes[0].rawValue;
                if (code) {
                  stream.getTracks().forEach(t => t.stop());
                  onScan(code);
                  return;
                }
              }
            } catch { /* ignore detection errors */ }
            if (!cancelled) requestAnimationFrame(scanLoop);
          };
          requestAnimationFrame(scanLoop);
        }
      } catch {
        if (!cancelled) setError('Camera not available. Use manual entry.');
      }
    };
    startCamera();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [onScan]);

  const handleManual = () => {
    const code = manualInput.trim().toUpperCase();
    if (code) {
      streamRef.current?.getTracks().forEach(t => t.stop());
      onScan(code);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl w-full max-w-md overflow-hidden border border-gray-600" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-bold text-white">Scan Barcode</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        {scanning && (
          <div className="relative bg-black" style={{ height: 240 }}>
            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-3/4 h-0.5 bg-red-500 opacity-70" />
            </div>
          </div>
        )}

        {error && <p className="text-yellow-400 text-sm text-center p-3">{error}</p>}

        <div className="p-4">
          <label className="block text-sm text-gray-400 mb-1">Or enter part number manually</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualInput}
              onChange={e => setManualInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleManual()}
              placeholder="Part number..."
              className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
              autoFocus={!!error}
            />
            <button
              onClick={handleManual}
              disabled={!manualInput.trim()}
              className="px-4 py-3 bg-yellow-400 text-gray-900 font-bold rounded-lg disabled:opacity-50"
            >
              Go
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Detail Modal ============

function DetailModal({
  item,
  onClose,
  onAdjust,
  onReceive,
}: {
  item: InventoryItem;
  onClose: () => void;
  onAdjust: () => void;
  onReceive: () => void;
}) {
  const isLow = item.minQuantity > 0 && item.onHand <= item.minQuantity;
  const isOut = item.onHand <= 0;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-600 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Large Image */}
        <div className="w-full aspect-square bg-gray-700 relative">
          {item.imageFilename ? (
            <img
              src={item.imageFilename}
              alt={item.partNumber}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-24 h-24 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          )}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center text-xl hover:bg-black/80"
          >
            &times;
          </button>
          {isOut ? (
            <span className="absolute top-3 left-3 px-3 py-1 rounded-lg text-sm font-bold bg-red-600 text-white">OUT OF STOCK</span>
          ) : isLow ? (
            <span className="absolute top-3 left-3 px-3 py-1 rounded-lg text-sm font-bold bg-yellow-500 text-gray-900">LOW STOCK</span>
          ) : null}
        </div>

        {/* Info */}
        <div className="p-5">
          <h2 className="text-xl font-bold text-white">{item.description}</h2>
          <p className="text-yellow-400 font-mono text-lg mt-1">{item.partNumber}</p>

          {item.supplierPartNumber && (
            <p className="text-gray-400 text-sm mt-1">Supplier: {item.supplierPartNumber}</p>
          )}

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="bg-gray-700/50 rounded-lg p-3 text-center">
              <div className="text-gray-400 text-xs uppercase">On Hand</div>
              <div className={`text-2xl font-bold mt-1 ${isOut ? 'text-red-400' : isLow ? 'text-yellow-400' : 'text-white'}`}>
                {formatQty(item.onHand, item.displayUnit, item.conversionFactor)}
              </div>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-3 text-center">
              <div className="text-gray-400 text-xs uppercase">Min Qty</div>
              <div className="text-2xl font-bold mt-1 text-white">
                {formatQty(item.minQuantity, item.displayUnit, item.conversionFactor)}
              </div>
            </div>
          </div>

          {(item.binLocation || item.inventoryGroup) && (
            <div className="grid grid-cols-2 gap-4 mt-3">
              {item.binLocation && (
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <div className="text-gray-400 text-xs uppercase">Bin</div>
                  <div className="text-white font-medium mt-1">{item.binLocation}</div>
                </div>
              )}
              {item.inventoryGroup && (
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <div className="text-gray-400 text-xs uppercase">Group</div>
                  <div className="text-white font-medium mt-1 capitalize">{item.inventoryGroup.replace(/_/g, ' ')}</div>
                </div>
              )}
            </div>
          )}

          {/* Last Updated */}
          {(() => {
            const { label, stale } = formatUpdatedAt(item.updatedAt);
            return (
              <div className={`mt-4 p-3 rounded-lg text-center text-sm ${stale ? 'bg-red-900/30 border border-red-700/50' : 'bg-gray-700/50'}`}>
                <span className={stale ? 'text-red-400 font-medium' : 'text-gray-400'}>
                  {stale && '⚠ '}{label}
                  {stale && ' — needs count this quarter'}
                </span>
              </div>
            );
          })()}

          {/* Actions */}
          <div className="flex gap-3 mt-5">
            <button
              onClick={onAdjust}
              className="flex-1 py-3 rounded-lg bg-gray-700 text-white font-medium hover:bg-gray-600 min-h-[48px]"
            >
              +/- Adjust
            </button>
            <button
              onClick={onReceive}
              className="flex-1 py-3 rounded-lg bg-green-600 text-white font-bold hover:bg-green-500 min-h-[48px] flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Receive
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Item Card ============

function ItemCard({
  item,
  highlighted,
  onTap,
  onAdjust,
  onReceive,
  cardRef,
}: {
  item: InventoryItem;
  highlighted: boolean;
  onTap: () => void;
  onAdjust: () => void;
  onReceive: () => void;
  cardRef?: React.Ref<HTMLDivElement>;
}) {
  const isLow = item.minQuantity > 0 && item.onHand <= item.minQuantity;
  const isOut = item.onHand <= 0;

  return (
    <div
      ref={cardRef}
      onClick={onTap}
      className={`bg-gray-800 rounded-lg border p-3 transition-all cursor-pointer active:scale-[0.98] ${
        highlighted
          ? 'border-yellow-400 ring-2 ring-yellow-400/50'
          : 'border-gray-700'
      }`}
    >
      <div className="flex gap-3">
        {/* Photo */}
        <div className="w-16 h-16 flex-shrink-0 bg-gray-700 rounded-lg overflow-hidden flex items-center justify-center">
          {item.imageFilename ? (
            <img
              src={item.imageFilename}
              alt={item.partNumber}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <svg className="w-8 h-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-white font-medium text-sm truncate">{item.description}</p>
              <p className="text-gray-400 text-xs">{item.partNumber}</p>
            </div>
            {isOut ? (
              <span className="flex-shrink-0 px-2 py-0.5 rounded text-xs font-bold bg-red-600 text-white">OUT</span>
            ) : isLow ? (
              <span className="flex-shrink-0 px-2 py-0.5 rounded text-xs font-bold bg-yellow-500 text-gray-900">LOW</span>
            ) : null}
          </div>

          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
            <span>
              On Hand: <span className={`font-medium ${isOut ? 'text-red-400' : isLow ? 'text-yellow-400' : 'text-white'}`}>
                {formatQty(item.onHand, item.displayUnit, item.conversionFactor)}
              </span>
            </span>
            {item.binLocation && (
              <span>Bin: <span className="text-gray-300">{item.binLocation}</span></span>
            )}
          </div>

          {/* Last Updated */}
          {(() => {
            const { label, stale } = formatUpdatedAt(item.updatedAt);
            return (
              <div className={`mt-1 text-xs ${stale ? 'text-red-400' : 'text-gray-500'}`}>
                {stale && <span className="mr-1">⚠</span>}{label}
              </div>
            );
          })()}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={e => { e.stopPropagation(); onAdjust(); }}
              className="px-3 py-1.5 rounded bg-gray-700 text-gray-300 text-xs font-medium hover:bg-gray-600 min-h-[36px]"
            >
              +/- Adjust
            </button>
            <button
              onClick={e => { e.stopPropagation(); onReceive(); }}
              className="px-3 py-1.5 rounded bg-green-700 text-white text-xs font-medium hover:bg-green-600 min-h-[36px] flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Receive
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Main Page ============

export function InventoryPage() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [groups, setGroups] = useState<InventoryGroup[]>([]);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  // Modal state
  const [detailItem, setDetailItem] = useState<InventoryItem | null>(null);
  const [adjustItem, setAdjustItem] = useState<InventoryItem | null>(null);
  const [receiveItem, setReceiveItem] = useState<InventoryItem | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  // Toast state
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);

  // Refs for scroll-to
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const LIMIT = 50;

  const addToast = (message: string, type: 'success' | 'error') => {
    const id = ++toastId.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };

  const dismissToast = (id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Debounced search
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  // Load groups on mount
  useEffect(() => {
    (async () => {
      try {
        const result = await api.getInventoryGroups();
        setGroups(result);
      } catch (err: any) {
        if (err?.message?.includes('Session expired')) {
          addToast('Session expired. Please log in again.', 'error');
        }
      }
    })();
  }, []);

  // Load items when filters change
  const loadItems = useCallback(async (newOffset = 0) => {
    if (newOffset === 0) setLoading(true);
    else setLoadingMore(true);

    try {
      const result = await api.getWorkstationInventoryItems({
        search: debouncedSearch,
        group: selectedGroup,
        lowStockOnly,
        limit: LIMIT,
        offset: newOffset,
      });

      if (newOffset === 0) {
        setItems(result.items);
      } else {
        setItems(prev => [...prev, ...result.items]);
      }
      setTotal(result.total);
      setOffset(newOffset);
    } catch {
      addToast('Failed to load inventory', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, selectedGroup, lowStockOnly]);

  useEffect(() => {
    loadItems(0);
  }, [loadItems]);

  // Handle barcode scan
  const handleBarcodeScan = useCallback(async (code: string) => {
    setShowScanner(false);
    // Search for the scanned code
    setSearch(code);
  }, []);

  // Handle adjust
  const handleAdjust = async (adjustmentType: 'add' | 'subtract', quantity: number, reason: string, notes: string) => {
    if (!adjustItem) return;
    try {
      await api.workstationAdjustStock({
        itemId: adjustItem.id,
        quantity,
        adjustmentType,
        reason,
        notes: notes || undefined,
      });

      // Update local state
      const delta = adjustmentType === 'add' ? quantity : -quantity;
      setItems(prev => prev.map(i =>
        i.id === adjustItem.id ? { ...i, onHand: i.onHand + delta } : i
      ));

      const hasUnit = adjustItem.displayUnit && adjustItem.displayUnit !== 'each' && adjustItem.conversionFactor > 1;
      const displayQty = hasUnit ? (quantity / adjustItem.conversionFactor).toFixed(1) + ' ' + adjustItem.displayUnit : String(quantity);
      addToast(`${adjustItem.partNumber} adjusted: ${adjustmentType === 'add' ? '+' : '-'}${displayQty}`, 'success');
      setAdjustItem(null);
    } catch (err: any) {
      addToast(err?.message || 'Adjustment failed', 'error');
    }
  };

  // Handle receive
  const handleReceive = async (quantity: number, quantityInDisplayUnits: boolean, poNumber: string, notes: string) => {
    if (!receiveItem) return;
    try {
      await api.workstationReceiveStock({
        itemId: receiveItem.id,
        quantity,
        quantityInDisplayUnits: quantityInDisplayUnits || undefined,
        poNumber: poNumber || undefined,
        notes: notes || undefined,
      });

      // Update local state
      const addQty = quantityInDisplayUnits && receiveItem.conversionFactor > 1
        ? quantity * receiveItem.conversionFactor
        : quantity;
      setItems(prev => prev.map(i =>
        i.id === receiveItem.id ? { ...i, onHand: i.onHand + addQty } : i
      ));

      addToast(`${receiveItem.partNumber}: received ${quantity}${quantityInDisplayUnits ? ` ${receiveItem.displayUnit}` : ''}`, 'success');
      setReceiveItem(null);
    } catch (err: any) {
      addToast(err?.message || 'Receive failed', 'error');
    }
  };

  // Highlight and scroll to item
  const scrollToItem = (id: string) => {
    setHighlightedId(id);
    setTimeout(() => {
      itemRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    setTimeout(() => setHighlightedId(null), 3000);
  };

  // Load more
  const hasMore = items.length < total;

  return (
    <div className="max-w-2xl mx-auto">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          Inventory
        </h1>
        <span className="text-sm text-gray-400">{total} items</span>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search part #, description..."
          className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
          >
            &times;
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setShowScanner(true)}
          className="px-3 py-2 rounded-lg bg-yellow-400 text-gray-900 font-medium text-sm flex items-center gap-1.5 min-h-[40px]"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h2v16H3V4zm4 0h1v16H7V4zm3 0h2v16h-2V4zm4 0h3v16h-3V4zm5 0h2v16h-2V4z" />
          </svg>
          Scan
        </button>

        <select
          value={selectedGroup}
          onChange={e => setSelectedGroup(e.target.value)}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-h-[40px] focus:border-yellow-400 focus:outline-none"
        >
          <option value="">All Groups</option>
          {groups.map(g => (
            <option key={g.name} value={g.name}>{g.name} ({g.itemCount})</option>
          ))}
        </select>

        <label className="flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm min-h-[40px] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={lowStockOnly}
            onChange={e => setLowStockOnly(e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 text-yellow-400 focus:ring-yellow-400 bg-gray-700"
          />
          <span className="text-yellow-400 font-medium">Low Stock</span>
        </label>
      </div>

      {/* Items List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12">
          <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p className="text-gray-400 text-sm">
            {debouncedSearch || selectedGroup || lowStockOnly
              ? 'No items match your filters'
              : 'No inventory items found'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <ItemCard
              key={item.id}
              item={item}
              highlighted={highlightedId === item.id}
              onTap={() => setDetailItem(item)}
              onAdjust={() => setAdjustItem(item)}
              onReceive={() => setReceiveItem(item)}
              cardRef={el => { itemRefs.current[item.id] = el; }}
            />
          ))}

          {/* Load More */}
          {hasMore && (
            <button
              onClick={() => loadItems(offset + LIMIT)}
              disabled={loadingMore}
              className="w-full py-3 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-700"
            >
              {loadingMore ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="sm" /> Loading...
                </span>
              ) : (
                `Load More (${items.length} of ${total})`
              )}
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      {detailItem && (
        <DetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onAdjust={() => { setAdjustItem(detailItem); setDetailItem(null); }}
          onReceive={() => { setReceiveItem(detailItem); setDetailItem(null); }}
        />
      )}
      {adjustItem && (
        <AdjustModal
          item={adjustItem}
          onClose={() => setAdjustItem(null)}
          onSubmit={handleAdjust}
        />
      )}
      {receiveItem && (
        <ReceiveModal
          item={receiveItem}
          onClose={() => setReceiveItem(null)}
          onSubmit={handleReceive}
        />
      )}
      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
}
